/**
 * =============================================================================
 * IDS CRM Web - Main Layout Component
 * =============================================================================
 *
 * PURPOSE:
 * Provides the main application layout with icon sidebar navigation.
 * Based on design screenshots - compact icon sidebar on left.
 *
 * =============================================================================
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ENGINEER_STATUS_META } from '../utils/status';
import {
  getDeviceLocation, getDeviceLocationDetailed,
  writeLocationStatus, readLocationStatus, locationStatusLabel,
  ENGINEER_STATUS_ROLES
} from '../utils/geolocation';
import GlobalSearch from './GlobalSearch';
import { getCapabilities } from '../utils/permissions';
import './Layout.css';

const ALERT_POLL_MS = 60000;
const VERSION_POLL_MS = 5 * 60000;

/**
 * New-build detection (UX feedback 2026-08-03 #1): single-page apps keep
 * running the JavaScript they loaded until a real reload, so a tab left
 * open misses releases — three "missing feature" reports in one week were
 * stale tabs. Polls the served index.html for the hashed bundle name and
 * offers a refresh when it changes. Dormant on the dev server (no hash).
 */
function useNewVersionAvailable() {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    let loadedBundle = null;
    let cancelled = false;

    const check = async () => {
      try {
        const response = await fetch('/', { cache: 'no-store' });
        const html = await response.text();
        const match = html.match(/static\/js\/main\.[a-z0-9]+\.js/);
        if (!match || cancelled) return;
        if (loadedBundle === null) {
          loadedBundle = match[0];
          return;
        }
        if (match[0] !== loadedBundle) setUpdateAvailable(true);
      } catch {
        // Offline or mid-deploy — try again next tick.
      }
    };

    check();
    const timer = setInterval(check, VERSION_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  return updateAvailable;
}

function timeAgo(isoString) {
  const seconds = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// Unassigned-count badge for the Inbox nav item (sales rework Phase 7).
function useInboxUnassignedCount(enabled) {
  const { authFetch } = useAuth();
  const [count, setCount] = useState(0);

  const fetchCount = useCallback(async () => {
    if (!enabled) return;
    try {
      const response = await authFetch('/api/v1/email-inbox?status=unassigned');
      if (!response.ok) return;
      const data = await response.json();
      setCount(data.unassigned_count || 0);
    } catch {
      // Silent - badge polling failures shouldn't disrupt the app
    }
  }, [authFetch, enabled]);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, ALERT_POLL_MS);
    return () => clearInterval(interval);
  }, [fetchCount]);

  return count;
}

function AlertBell() {
  const { authFetch } = useAuth();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [panelOpen, setPanelOpen] = useState(false);
  const panelRef = useRef(null);

  const fetchAlerts = useCallback(async () => {
    try {
      const response = await authFetch('/api/v1/alerts?unread=true&limit=20');
      if (!response.ok) return;
      const data = await response.json();
      setAlerts(data.data || []);
      setUnreadCount(data.unread_count || 0);
    } catch {
      // Silent - alert polling failures shouldn't disrupt the app
    }
  }, [authFetch]);

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, ALERT_POLL_MS);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  useEffect(() => {
    if (!panelOpen) return undefined;
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setPanelOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [panelOpen]);

  const handleAlertClick = async (alert) => {
    setAlerts(prev => prev.filter(a => a.alert_id !== alert.alert_id));
    setUnreadCount(prev => Math.max(0, prev - 1));
    setPanelOpen(false);
    try {
      await authFetch(`/api/v1/alerts/${alert.alert_id}/read`, { method: 'PATCH' });
    } catch {
      // Best-effort - navigation still proceeds even if the mark-read call fails
    }
    if (alert.link_path) {
      navigate(alert.link_path);
    }
  };

  const handleMarkAllRead = async () => {
    setAlerts([]);
    setUnreadCount(0);
    try {
      await authFetch('/api/v1/alerts/read-all', { method: 'PATCH' });
    } catch {
      // Best-effort
    }
  };

  return (
    <div className="alert-bell-wrapper" ref={panelRef}>
      <button
        className="nav-icon alert-bell-icon"
        title="Alerts"
        onClick={() => setPanelOpen(open => !open)}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 01-3.46 0"/>
        </svg>
        {unreadCount > 0 && (
          <span className="alert-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {panelOpen && (
        <div className="alert-panel">
          <div className="alert-panel-header">
            <span>Alerts</span>
            {alerts.length > 0 && (
              <button className="alert-mark-all" onClick={handleMarkAllRead}>Mark all read</button>
            )}
          </div>
          <div className="alert-panel-list">
            {alerts.length === 0 && (
              <div className="alert-panel-empty">No unread alerts</div>
            )}
            {alerts.map(alert => (
              <button
                key={alert.alert_id}
                className="alert-panel-item"
                onClick={() => handleAlertClick(alert)}
              >
                <div className="alert-panel-item-title">{alert.title}</div>
                <div className="alert-panel-item-message">{alert.message}</div>
                <div className="alert-panel-item-time">{timeAgo(alert.created_at)}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const ACTIVE_TRACKING_STATUSES = ['available', 'travelling', 'on_site', 'travelling_home', 'on_job', 'on_call'];
const STATUS_POLL_MS = 60000;
const LOCATION_HEARTBEAT_MS = 5 * 60000;

/**
 * Sidebar clock in/out widget for engineer users. Shows the engineer's live
 * status as a coloured dot; the panel offers Clock In / Clock Out. While the
 * app is open and the engineer is on duty, a location heartbeat pings every
 * few minutes (browser apps cannot track in the background - stage stamps and
 * this foreground heartbeat are the available coverage).
 */
function EngineerClock() {
  const { authFetch, user } = useAuth();
  const [status, setStatus] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const panelRef = useRef(null);

  const isEngineer = ENGINEER_STATUS_ROLES.includes(user?.role);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await authFetch('/api/v1/engineers/me/status');
      if (!response.ok) return;
      const data = await response.json();
      setStatus(data.current_status);
    } catch {
      // Silent - status polling failures shouldn't disrupt the app
    }
  }, [authFetch]);

  useEffect(() => {
    if (!isEngineer) return undefined;
    fetchStatus();
    const interval = setInterval(fetchStatus, STATUS_POLL_MS);
    return () => clearInterval(interval);
  }, [fetchStatus, isEngineer]);

  // Foreground location heartbeat while on duty
  useEffect(() => {
    if (!isEngineer || !status || !ACTIVE_TRACKING_STATUSES.includes(status)) return undefined;
    const ping = async () => {
      const { location, reason } = await getDeviceLocationDetailed();
      if (!location) {
        writeLocationStatus(reason || 'unavailable');
        return;
      }
      try {
        const response = await authFetch('/api/v1/engineers/me/location', {
          method: 'POST',
          body: JSON.stringify(location)
        });
        writeLocationStatus(response.ok ? 'sent' : 'send_failed', response.ok ? null : `HTTP ${response.status}`);
      } catch {
        writeLocationStatus('send_failed', 'network');
      }
    };
    ping();
    const interval = setInterval(ping, LOCATION_HEARTBEAT_MS);
    return () => clearInterval(interval);
  }, [isEngineer, status, authFetch]);

  useEffect(() => {
    if (!panelOpen) return undefined;
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setPanelOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [panelOpen]);

  const handleClock = async (action) => {
    setBusy(true);
    try {
      const location = await getDeviceLocation();
      const response = await authFetch('/api/v1/engineers/me/clock', {
        method: 'POST',
        body: JSON.stringify({ action, ...(location || {}) })
      });
      if (response.ok) {
        const data = await response.json();
        setStatus(data.current_status);
        setPanelOpen(false);
      }
    } catch {
      // Leave status as-is; next poll corrects it
    } finally {
      setBusy(false);
    }
  };

  if (!isEngineer) return null;

  const meta = ENGINEER_STATUS_META[status] || { label: 'Unknown', color: '#9CA3AF' };
  const clockedOn = status && status !== 'off_duty' && status !== 'annual_leave' && status !== 'sick';
  const locationStatus = clockedOn ? readLocationStatus() : null;
  const locationLine = locationStatusLabel(locationStatus);

  return (
    <div className="alert-bell-wrapper engineer-clock-wrapper" ref={panelRef}>
      <button
        className="nav-icon engineer-clock-icon"
        title={`My status: ${meta.label}`}
        onClick={() => setPanelOpen(open => !open)}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="9"/>
          <polyline points="12 7 12 12 15 14"/>
        </svg>
        <span className="engineer-clock-dot" style={{ background: meta.color }} />
      </button>

      {panelOpen && (
        <div className="alert-panel engineer-clock-panel">
          <div className="alert-panel-header">
            <span>My Status</span>
          </div>
          <div className="engineer-clock-body">
            <div className="engineer-clock-status">
              <span className="engineer-clock-dot-lg" style={{ background: meta.color }} />
              {meta.label}
            </div>
            {clockedOn ? (
              <button className="engineer-clock-btn out" disabled={busy} onClick={() => handleClock('out')}>
                {busy ? 'Working...' : 'Clock Out'}
              </button>
            ) : (
              <button className="engineer-clock-btn in" disabled={busy} onClick={() => handleClock('in')}>
                {busy ? 'Working...' : 'Clock In'}
              </button>
            )}
            {locationLine && (
              <p className={`engineer-clock-location ${locationStatus?.state === 'sent' ? 'ok' : 'warn'}`}>
                {locationLine}
              </p>
            )}
            <p className="engineer-clock-hint">
              Clocking in shares your location while on duty. Job stages (travelling, on site) update
              automatically from your work logs.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  // Ctrl+K / Cmd+K opens global search
  useEffect(() => {
    const onKey = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // R4c (W10): nav renders from the permission matrix — server-issued
  // capabilities at login, same matrix as the API's authorize() buckets.
  // Desktop and mobile use the SAME booleans, so they can no longer drift.
  const caps = getCapabilities(user);
  const inboxCount = useInboxUnassignedCount(caps.use_inbox);
  const updateAvailable = useNewVersionAvailable();

  // Per-user sidebar hiding (2026-08-08), edited in Settings → My Menu.
  // Applies to the DESKTOP sidebar only; the mobile More sheet stays
  // complete so nothing is unreachable.
  const hiddenNav = new Set(user?.ui_preferences?.hidden_nav || []);
  const navVisible = (key) => !hiddenNav.has(key);

  return (
    <div className="app-shell">
      {updateAvailable && (
        <div className="update-banner" role="status">
          <span>A new version of IDS CRM is available.</span>
          <button className="update-banner-btn" onClick={() => window.location.reload()}>
            Refresh now
          </button>
        </div>
      )}
      {/* Icon Sidebar */}
      <nav className="sidebar">
        <div className="sidebar-logo" role="img" aria-label="IDS - Intelligent Dairy Solutions" />

        <AlertBell />
        <EngineerClock />

        <button className="nav-icon" title="Search (Ctrl+K)" onClick={() => setSearchOpen(true)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <span className="nav-label">Search</span>
        </button>

        <div className="nav-sep"></div>

        {/* R2 (DL-10): engineers land on Today; the Dashboard's cards are money surfaces */}
        {caps.view_dashboard && (
          <NavLink to="/" className={({ isActive }) => `nav-icon ${isActive ? 'active' : ''}`} title="Dashboard" end>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
              <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
          <span className="nav-label">Dashboard</span></NavLink>
        )}

        {caps.use_today && (
          <NavLink to="/today" className={({ isActive }) => `nav-icon ${isActive ? 'active' : ''}`} title="Today">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
          <span className="nav-label">Today</span></NavLink>
        )}

        {navVisible('customers') && (
        <NavLink to="/customers" className={({ isActive }) => `nav-icon ${isActive ? 'active' : ''}`} title="Customers">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4z"/>
          </svg>
        <span className="nav-label">Customers</span></NavLink>
        )}

        {navVisible('distributors') && (caps.view_distributors || caps.manage_distributors) && (
          <NavLink to="/distributors" className={({ isActive }) => `nav-icon ${isActive ? 'active' : ''}`} title="Distributors">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="5" r="3"/>
              <line x1="12" y1="8" x2="12" y2="11"/>
              <circle cx="6" cy="17" r="3"/>
              <circle cx="18" cy="17" r="3"/>
              <line x1="12" y1="11" x2="6" y2="14"/>
              <line x1="12" y1="11" x2="18" y2="14"/>
            </svg>
          <span className="nav-label">Distributors</span></NavLink>
        )}

        {navVisible('pipeline') && caps.use_pipeline && (
          <NavLink to="/pipeline" className={({ isActive }) => `nav-icon ${isActive ? 'active' : ''}`} title="Commercial">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 20V10M12 20V4M6 20v-6"/>
            </svg>
          <span className="nav-label">Commercial</span></NavLink>
        )}

        {navVisible('inbox') && caps.use_inbox && (
          <NavLink to="/inbox" className={({ isActive }) => `nav-icon ${isActive ? 'active' : ''}`} title="Inbox">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 12h-6l-2 3h-4l-2-3H2"/>
              <path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z"/>
            </svg>
            {inboxCount > 0 && <span className="alert-badge">{inboxCount > 99 ? '99+' : inboxCount}</span>}
          <span className="nav-label">Inbox</span></NavLink>
        )}

        {navVisible('tickets') && caps.use_service_desk !== false && (
        <NavLink to="/tickets" className={({ isActive }) => `nav-icon ${isActive ? 'active' : ''}`} title="Jobs & Tickets">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/>
          </svg>
        <span className="nav-label">Jobs & Tickets</span></NavLink>
        )}

        {navVisible('assets') && (
        <NavLink to="/assets" className={({ isActive }) => `nav-icon ${isActive ? 'active' : ''}`} title="Assets">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="4" y="4" width="16" height="16" rx="2"/>
            <rect x="9" y="9" width="6" height="6"/>
            <line x1="9" y1="1" x2="9" y2="4"/>
            <line x1="15" y1="1" x2="15" y2="4"/>
            <line x1="9" y1="20" x2="9" y2="23"/>
            <line x1="15" y1="20" x2="15" y2="23"/>
            <line x1="20" y1="9" x2="23" y2="9"/>
            <line x1="20" y1="14" x2="23" y2="14"/>
            <line x1="1" y1="9" x2="4" y2="9"/>
            <line x1="1" y1="14" x2="4" y2="14"/>
          </svg>
        <span className="nav-label">Assets</span></NavLink>
        )}

        <div className="nav-sep"></div>

        {navVisible('scheduling') && caps.use_scheduling !== false && (
        <NavLink to="/tasks" className={({ isActive }) => `nav-icon ${isActive ? 'active' : ''}`} title="Scheduling">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
        <span className="nav-label">Scheduling</span></NavLink>
        )}

        {navVisible('ops_board') && caps.use_ops_board !== false && (
        <NavLink to="/ops-board" className={({ isActive }) => `nav-icon ${isActive ? 'active' : ''}`} title="Ops Planning">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="5" height="18" rx="1"/>
            <rect x="10" y="3" width="5" height="12" rx="1"/>
            <rect x="17" y="3" width="4" height="8" rx="1"/>
          </svg>
        <span className="nav-label">Ops Planning</span></NavLink>
        )}

        {navVisible('engineers') && caps.see_team && (
          <NavLink to="/engineers" className={({ isActive }) => `nav-icon ${isActive ? 'active' : ''}`} title="Engineers">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
            </svg>
          <span className="nav-label">Engineers</span></NavLink>
        )}

        {navVisible('reports') && caps.view_reports && (
          <NavLink to="/reports" className={({ isActive }) => `nav-icon ${isActive ? 'active' : ''}`} title="Reports">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="20" x2="18" y2="10"/>
              <line x1="12" y1="20" x2="12" y2="4"/>
              <line x1="6" y1="20" x2="6" y2="14"/>
            </svg>
          <span className="nav-label">Reports</span></NavLink>
        )}

        {navVisible('map') && caps.use_map && (
          <NavLink to="/map" className={({ isActive }) => `nav-icon ${isActive ? 'active' : ''}`} title="Map">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="10" r="3"/>
              <path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 10-16 0c0 3 2.7 7 8 11.7z"/>
            </svg>
          <span className="nav-label">Map</span></NavLink>
        )}

        {navVisible('on_call') && caps.use_on_call && (
          <NavLink to="/on-call" className={({ isActive }) => `nav-icon ${isActive ? 'active' : ''}`} title="On Call">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 16.92v3a2 2 0 01-2.18 2 19.8 19.8 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.8 19.8 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.12.9.32 1.77.59 2.61a2 2 0 01-.45 2.11L8 9.69a16 16 0 006.31 6.31l1.25-1.25a2 2 0 012.11-.45c.84.27 1.71.47 2.61.59A2 2 0 0122 16.92z"/>
            </svg>
          <span className="nav-label">On Call</span></NavLink>
        )}

        {/* RR2: Financial reach payroll directly — they hold view_payroll
            but not use_on_call, so the rota-first route would 403 on them. */}
        {navVisible('payroll') && caps.view_payroll && !caps.use_on_call && (
          <NavLink to="/on-call/payroll" className={({ isActive }) => `nav-icon ${isActive ? 'active' : ''}`} title="Payroll">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="7" width="20" height="14" rx="2"/>
              <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/>
              <line x1="12" y1="11" x2="12" y2="17"/>
              <path d="M14.5 12.5c-.5-.8-1.5-1-2.5-.7s-1.5 1.2-1 2c.6 1 2.4.7 3 1.7.5.8 0 1.7-1 2s-2-.1-2.5-.9"/>
            </svg>
          <span className="nav-label">Payroll</span></NavLink>
        )}

        {navVisible('approvals') && (
        <NavLink to="/approvals" className={({ isActive }) => `nav-icon ${isActive ? 'active' : ''}`} title="Approvals">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 11l3 3L22 4"/>
            <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
          </svg>
        <span className="nav-label">Approvals</span></NavLink>
        )}

        {/* Accommodation directory (2026-08-07): every role reads it —
            bookers and engineers on the road need the same warnings. */}
        {navVisible('stays') && (
        <NavLink to="/accommodation" className={({ isActive }) => `nav-icon ${isActive ? 'active' : ''}`} title="Accommodation">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M2 4v16"/>
            <path d="M2 8h18a2 2 0 012 2v10"/>
            <path d="M2 17h20"/>
            <path d="M6 8v9"/>
          </svg>
        <span className="nav-label">Stays</span></NavLink>
        )}

        {/* Engineer bulletins (2026-09-13): fixes for repeated faults. Every
            role browses and writes; reviewers publish inside the page. */}
        {navVisible('bulletins') && (
        <NavLink to="/bulletins" className={({ isActive }) => `nav-icon ${isActive ? 'active' : ''}`} title="Bulletins">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 11v2a1 1 0 001 1h2l6 4V6L6 10H4a1 1 0 00-1 1z"/>
            <path d="M16 9a4 4 0 010 6"/>
            <path d="M19 6a8 8 0 010 12"/>
          </svg>
        <span className="nav-label">Bulletins</span></NavLink>
        )}

        {navVisible('help') && (
        <NavLink to="/help" className={({ isActive }) => `nav-icon ${isActive ? 'active' : ''}`} title="Help">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        <span className="nav-label">Help</span></NavLink>
        )}

        <div className="nav-sep"></div>

        {/* Settings is for everyone since 2026-08-10: the personal tabs
            (My Account, My Menu) show for all roles; admin tabs stay gated
            per role inside the page. */}
        <NavLink to="/admin" className={({ isActive }) => `nav-icon ${isActive ? 'active' : ''}`} title="Settings">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/>
            </svg>
          <span className="nav-label">Settings</span></NavLink>

        <div className="nav-spacer"></div>

        {/* My Profile has no sidebar entry (Gareth 2026-08-10): your own
            record lives at Settings → My Profile; managers open other
            people's via the Engineers page. One icon fewer for everyone. */}
        <button className="nav-icon logout-icon" onClick={handleLogout} title="Sign Out">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
          <span className="nav-label">Sign Out</span>
        </button>
      </nav>

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* Mobile bottom nav: 5 large targets; "More" opens the full menu */}
      <nav className="mobile-nav">
        {caps.view_dashboard && (
          <NavLink to="/" className={({ isActive }) => `mobile-nav-item ${isActive ? 'active' : ''}`} end>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
            <span>Home</span>
          </NavLink>
        )}
        {caps.use_today ? (
          <NavLink to="/today" className={({ isActive }) => `mobile-nav-item ${isActive ? 'active' : ''}`}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            <span>Today</span>
          </NavLink>
        ) : (
          <NavLink to="/pipeline" className={({ isActive }) => `mobile-nav-item ${isActive ? 'active' : ''}`}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>
            <span>Commercial</span>
          </NavLink>
        )}
        {caps.use_service_desk !== false ? (
        <NavLink to="/tickets" className={({ isActive }) => `mobile-nav-item ${isActive ? 'active' : ''}`}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>
          <span>Jobs</span>
        </NavLink>
        ) : (
        <NavLink to="/customers" className={({ isActive }) => `mobile-nav-item ${isActive ? 'active' : ''}`}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          <span>Customers</span>
        </NavLink>
        )}
        <button className="mobile-nav-item" onClick={() => setSearchOpen(true)}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <span>Search</span>
        </button>
        <button className="mobile-nav-item" onClick={() => setMoreOpen(true)}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg>
          <span>More</span>
        </button>
      </nav>

      {moreOpen && (
        <div className="mobile-more-overlay" onClick={() => setMoreOpen(false)}>
          <div className="mobile-more-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-more-grab" />
            {[
              { to: '/customers', label: 'Customers', show: true },
              { to: '/distributors', label: 'Distributors', show: caps.view_distributors || caps.manage_distributors },
              { to: '/pipeline', label: 'Commercial', show: caps.use_pipeline },
              { to: '/assets', label: 'Assets', show: true },
              { to: '/tasks', label: 'Scheduling', show: caps.use_scheduling !== false },
              { to: '/ops-board', label: 'Ops Planning', show: caps.use_ops_board !== false },
              { to: '/engineers', label: 'Engineers', show: caps.see_team },
              { to: '/reports', label: 'Reports', show: caps.view_reports },
              { to: '/map', label: 'Map', show: caps.use_map },
              { to: '/on-call', label: 'On Call', show: caps.use_on_call },
              { to: '/on-call/payroll', label: 'Payroll', show: !!caps.view_payroll && !caps.use_on_call },
              { to: '/inbox', label: 'Inbox', show: caps.use_inbox },
              { to: '/accommodation', label: 'Accommodation', show: true },
              { to: '/bulletins', label: 'Bulletins', show: true },
              { to: '/approvals', label: 'Approvals', show: caps.use_approvals },
              { to: '/help', label: 'Help', show: true },
              { to: '/admin?tab=profile', label: 'My Profile', show: true },
              { to: '/admin', label: 'Settings', show: true },
              { to: '/admin?tab=menu', label: 'Customise menu', show: true }
            ].filter(item => item.show).map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                className="mobile-more-link"
                onClick={() => setMoreOpen(false)}
              >
                {item.label}
              </NavLink>
            ))}
            {/* The More sheet deliberately lists EVERYTHING the role can
                reach — sidebar hiding is desktop-only, so a hidden item is
                never unreachable on a phone. */}
            <button className="mobile-more-link mobile-more-signout" onClick={handleLogout}>Sign Out</button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="main-content">
        {children}
      </main>
    </div>
  );
}

export default Layout;
